import React, { useState, useEffect, useCallback } from 'react';
import { XCircle, AlertCircle, Wifi } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/v2/api';
const IMG_BASE = API_BASE.replace('/v2/api', '');

function useNow() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return now;
}

const cap = str => str ? str.charAt(0).toUpperCase() + str.slice(1) : '';
const fmtDate = (iso, fmt = 'EEEE d MMMM') => cap(format(parseISO(iso), fmt, { locale: fr }));
const fmtHeure = h => h?.slice(0, 5) ?? '';

const STATUT = {
  libre:     { dot: '#10B981', label: 'Libre' },
  pending:   { dot: '#F59E0B', label: 'En attente' },
  confirmed: { dot: '#3B82F6', label: 'Confirmé' },
};

export default function DisplayPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [loading, setLoading] = useState(true);
  const now = useNow();

  const fetchData = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/display/summary`);
      if (!r.ok) {
        if (r.status === 403) {
          const body = await r.json().catch(() => ({}));
          throw new Error(body.error === 'local_only' ? 'local_only' : 'disabled');
        }
        throw new Error('error');
      }
      const json = await r.json();
      setData(json);
      setError(null);
      setLastRefresh(new Date());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!data) return;
    const secs = data.settings?.refreshSeconds || 60;
    const t = setInterval(fetchData, secs * 1000);
    return () => clearInterval(t);
  }, [data, fetchData]);

  if (loading) return (
    <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F0F4F8' }}>
      <div style={{ textAlign: 'center', color: '#6B7280' }}>
        <Wifi style={{ width: 40, height: 40, margin: '0 auto 12px', display: 'block' }} />
        <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>Chargement du tableau d'affichage…</div>
      </div>
    </div>
  );

  if (error === 'local_only') return (
    <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0F172A' }}>
      <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)', maxWidth: 420 }}>
        <XCircle style={{ width: 48, height: 48, margin: '0 auto 16px', display: 'block', color: '#EF4444' }} />
        <div style={{ fontSize: '1.2rem', fontWeight: 700, color: 'white', marginBottom: 10 }}>Accès restreint</div>
        <div style={{ fontSize: '0.9rem', lineHeight: 1.6 }}>Cette page est réservée au réseau local du club.<br />Elle n'est accessible que depuis le site du Stamm.</div>
      </div>
    </div>
  );

  if (error === 'disabled') return (
    <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0F172A' }}>
      <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>
        <XCircle style={{ width: 40, height: 40, margin: '0 auto 12px', display: 'block', color: '#EF4444' }} />
        <div style={{ fontSize: '1.1rem', fontWeight: 600, color: 'white' }}>Tableau d'affichage désactivé</div>
        <div style={{ fontSize: '0.9rem', marginTop: 6 }}>Activez-le dans les paramètres admin.</div>
      </div>
    </div>
  );

  if (error) return (
    <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F0F4F8' }}>
      <div style={{ textAlign: 'center', color: '#6B7280' }}>
        <AlertCircle style={{ width: 40, height: 40, margin: '0 auto 12px', display: 'block', color: '#F59E0B' }} />
        <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>Impossible de charger les données</div>
        <button onClick={fetchData} style={{ marginTop: 14, padding: '8px 20px', background: '#111827', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>Réessayer</button>
      </div>
    </div>
  );

  const { qso = [], evenements = [], annonces = [], stamm = [], settings = {} } = data || {};
  const nextQSO = qso[0];
  const upcomingQSO = qso.slice(1, 5);

  return (
    <div style={{ height: '100vh', background: '#0F172A', fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif', display: 'flex', flexDirection: 'column', overflow: 'hidden', color: 'white' }}>

      {/* ── BANNIÈRE (photo seule, pleine largeur) ── */}
      <img src="/banner.jpg" alt="HB9G" style={{ width: '100%', height: 'auto', display: 'block', flexShrink: 0 }} />

      {/* ── BARRE HORLOGE + MESSAGE ── */}
      <div style={{ flexShrink: 0, background: '#1E293B', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 28px' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: '0.65rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#0EA5E9', background: 'rgba(14,165,233,0.15)', border: '1px solid rgba(14,165,233,0.4)', padding: '3px 10px', borderRadius: 5, flexShrink: 0 }}>Bienvenue</span>
          <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'white' }}>Bonsoir à tous au Stamm HB9G ! Retrouvez ici les prochains QSO de section, l'agenda du club et les annonces des membres.</span>
        </span>
        <div style={{ flexShrink: 0, marginLeft: 24, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontFamily: 'monospace', fontSize: '1.6rem', fontWeight: 900, color: 'white', letterSpacing: '0.06em' }}>{format(now, 'HH:mm:ss')}</span>
          <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.45)' }}>{cap(format(now, 'EEEE d MMMM yyyy', { locale: fr }))}</span>
        </div>
      </div>

      {/* ── BODY : layout 3 colonnes ── */}
      <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: '2fr 1.2fr 1.4fr', gap: 0, overflow: 'hidden' }}>

        {/* ── COL 1 : QSO (mise en avant) ── */}
        <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRight: '1px solid rgba(255,255,255,0.07)' }}>
          {/* Titre section */}
          <div style={{ padding: '14px 22px 10px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)' }}>QSO de section</span>
          </div>

          {/* Prochain QSO mis en avant */}
          {nextQSO && (
            <div style={{ margin: '14px 22px', padding: '16px 20px', background: 'rgba(29,78,216,0.15)', border: '1px solid rgba(29,78,216,0.4)', borderRadius: 10, borderLeft: '4px solid #3B82F6', flexShrink: 0 }}>
              <div style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#60A5FA', marginBottom: 6 }}>Prochain QSO</div>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'white', lineHeight: 1.1 }}>{fmtDate(nextQSO.date)}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
                <span style={{ fontFamily: 'monospace', fontSize: '1rem', color: 'rgba(255,255,255,0.6)' }}>{fmtHeure(nextQSO.heure)}</span>
                {nextQSO.capitaine_indicatif
                  ? <span style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '1rem', color: '#60A5FA', background: 'rgba(59,130,246,0.15)', padding: '2px 10px', borderRadius: 5 }}>{nextQSO.capitaine_indicatif}</span>
                  : <span style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.3)', fontStyle: 'italic' }}>Capitaine non désigné</span>
                }
                {(() => { const s = STATUT[nextQSO.statut]; return s ? <span style={{ fontSize: '0.72rem', fontWeight: 700, color: s.dot }}><span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: s.dot, marginRight: 5 }} />{s.label}</span> : null; })()}
              </div>
            </div>
          )}

          {/* Sessions suivantes */}
          <div style={{ flex: 1, minHeight: 0, overflowY: 'hidden', padding: '0 22px 14px', display: 'flex', flexDirection: 'column', gap: 1 }}>
            {upcomingQSO.length === 0 && !nextQSO && (
              <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.85rem', padding: '20px 0', textAlign: 'center' }}>Aucune session prévue</div>
            )}
            {upcomingQSO.map((q, i) => {
              const s = STATUT[q.statut] || {};
              return (
                <div key={q.id} style={{ display: 'flex', alignItems: 'center', padding: '10px 12px', borderRadius: 8, background: i % 2 === 0 ? 'rgba(255,255,255,0.03)' : 'transparent', gap: 12 }}>
                  <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: s.dot || '#6B7280', flexShrink: 0, marginTop: 1 }} />
                  <div style={{ flex: 1 }}>
                    <span style={{ fontWeight: 600, fontSize: '0.9rem', color: 'rgba(255,255,255,0.85)' }}>{fmtDate(q.date)}</span>
                    <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.35)', marginLeft: 10 }}>{fmtHeure(q.heure)}</span>
                  </div>
                  {q.capitaine_indicatif
                    ? <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.78rem', color: '#93C5FD' }}>{q.capitaine_indicatif}</span>
                    : <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.25)', fontStyle: 'italic' }}>Capitaine non désigné</span>
                  }
                </div>
              );
            })}
          </div>
        </div>

        {/* ── COL 2 : Agenda seul ── */}
        <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRight: '1px solid rgba(255,255,255,0.07)' }}>
          <div style={{ padding: '14px 18px 10px', flexShrink: 0 }}>
            <span style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)' }}>Agenda — Prochaines activités</span>
          </div>
          <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', padding: '0 18px 14px', display: 'flex', flexDirection: 'column', gap: 7 }}>
            {evenements.length === 0 ? (
              <div style={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.82rem', textAlign: 'center', padding: '16px 0' }}>Aucun événement</div>
            ) : evenements.slice(0, 5).map(e => (
              <div key={e.id} style={{ padding: '10px 12px', borderRadius: 8, borderLeft: '3px solid #10B981', background: 'rgba(16,185,129,0.07)', flexShrink: 0 }}>
                <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'rgba(255,255,255,0.9)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.titre}</div>
                <div style={{ fontSize: '0.72rem', color: '#34D399', marginTop: 3 }}>
                  {fmtDate(e.date_debut)}
                  {e.lieu && <span style={{ color: 'rgba(255,255,255,0.35)', marginLeft: 6 }}>· {e.lieu}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── COL 3 : Stamm (haut) + Annonces (bas) ── */}
        <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {/* Stamm */}
          <div style={{ flexShrink: 0, padding: '14px 18px 12px', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
            <div style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', marginBottom: 10 }}>Stamm — Prochaines ouvertures</div>
            {stamm.length === 0 ? (
              <div style={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.82rem' }}>Aucune session</div>
            ) : stamm.slice(0, 2).map(s => (
              <div key={s.id} style={{ padding: '10px 12px', borderRadius: 8, borderLeft: '3px solid #A78BFA', background: 'rgba(167,139,250,0.09)', marginBottom: 7 }}>
                <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'rgba(255,255,255,0.9)' }}>{fmtDate(s.date)} — {fmtHeure(s.heure)}</div>
                {s.responsable_indicatif
                  ? <div style={{ fontSize: '0.72rem', color: '#C4B5FD', marginTop: 3 }}><span style={{ fontFamily: 'monospace', fontWeight: 800 }}>{s.responsable_indicatif}</span> · {s.responsable_prenom} {s.responsable_nom}</div>
                  : <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.25)', marginTop: 3 }}>Responsable non désigné</div>
                }
              </div>
            ))}
          </div>

          {/* Annonces */}
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '12px 18px 8px', flexShrink: 0 }}>
              <span style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)' }}>Top 5 — Dernières annonces publiées</span>
            </div>
            <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', padding: '0 18px 8px', display: 'flex', flexDirection: 'column', gap: 4 }}>
              {annonces.length === 0 ? (
                <div style={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.82rem', textAlign: 'center', padding: '10px 0' }}>Aucune annonce</div>
              ) : annonces.slice(0, 5).map(a => (
                <div key={a.id} style={{ display: 'flex', gap: 8, padding: '6px 10px', borderRadius: 6, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.07)', alignItems: 'center', flex: 1, minHeight: 0 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.82rem', color: 'rgba(255,255,255,0.9)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.titre}</div>
                    <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.35)', marginTop: 1 }}>
                      {a.auteur_indicatif && <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#FCD34D' }}>{a.auteur_indicatif}</span>}
                      {a.localisation && <span style={{ marginLeft: 5 }}>· {a.localisation}</span>}
                    </div>
                  </div>
                  {Number(a.prix) > 0
                    ? <span style={{ fontWeight: 800, fontSize: '0.78rem', color: '#FCD34D', flexShrink: 0 }}>{Number(a.prix).toLocaleString('fr-CH')} <span style={{ fontSize: '0.6rem', opacity: 0.7 }}>CHF</span></span>
                    : Number(a.prix) === 0
                      ? <span style={{ fontWeight: 700, fontSize: '0.68rem', color: '#34D399', flexShrink: 0 }}>Gratuit</span>
                      : null
                  }
                </div>
              ))}
            </div>
          </div>

        </div>

      </div>

      {/* ── FOOTER ── */}
      <div style={{ flexShrink: 0, background: 'rgba(255,255,255,0.04)', borderTop: '1px solid rgba(255,255,255,0.07)', padding: '5px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.25)', letterSpacing: '0.05em' }}>HB9G · JN36BK · https://live.hb9g.ch:10443</span>
        {lastRefresh && <span style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.2)' }}>Mis à jour {format(lastRefresh, 'HH:mm:ss')} · refresh {data?.settings?.refreshSeconds || 60}s</span>}
      </div>

    </div>
  );
}
